import { isEqual } from "lodash";

/**
 * Get the differences between two objects.
 *
 * @param obj1 - The first object.
 * @param obj2 - The second object.
 * @param returnChange - Whether to return the changed values only.
 * @returns An object containing the differences, or undefined if the objects are equal.
 */
export function getObjectDiff<T extends object>(obj1: T, obj2: T, returnChange: true): Partial<T>;
/**
 * Get the differences between two objects.
 *
 * @param obj1 - The first object.
 * @param obj2 - The second object.
 * @param returnChange - Whether to return the changed values only.
 * @returns When `returnChange` is true returns an object (or array) with values from `obj2` for keys that changed; when false returns paired values `[old, new]` or `undefined` if no differences.
 */
export function getObjectDiff(obj1: unknown, obj2: unknown, returnChange?: false): unknown;
/**
 * Get the differences between two objects.
 *
 * @param obj1 - The first object.
 * @param obj2 - The second object.
 * @param returnChange - Whether to return the changed values only.
 * @returns An object containing the differences, or undefined if the objects are equal.
 */
export function getObjectDiff<T extends object>(
  obj1: T,
  obj2: T,
  returnChange?: boolean,
): Partial<T> | unknown {
  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return obj1 !== obj2 ? [obj1, obj2] : undefined;
  }
  const keys1: string[] = Object.keys(obj1);
  const keys2: string[] = Object.keys(obj2);
  const uniqueKeys = new Set([...keys1, ...keys2]);

  /* Set up the return object */
  const isArray = Array.isArray(obj1);
  const differences: Record<string, unknown> = {};

  /* Loop through the keys and compare values */
  for (const key of uniqueKeys) {
    const value1 = obj1[key as keyof typeof obj1] as unknown;
    const value2 = obj2[key as keyof typeof obj2] as unknown;

    if (
      typeof value1 === "object" &&
      value1 !== null &&
      typeof value2 === "object" &&
      value2 !== null &&
      !returnChange
    ) {
      const nestedDifferences = getObjectDiff(value1, value2, false);
      if (nestedDifferences) {
        differences[key] = nestedDifferences;
      }
    } else if (
      typeof value1 === "object" &&
      value1 !== null &&
      typeof value2 === "object" &&
      value2 !== null &&
      returnChange &&
      !isEqual(value1, value2)
    ) {
      differences[key] = value2;
    } else if (
      !(
        typeof value1 === "object" &&
        value1 !== null &&
        typeof value2 === "object" &&
        value2 !== null
      ) &&
      value1 !== value2
    ) {
      /* Add any differences according to the return type */
      differences[key] = returnChange ? value2 : [value1, value2];
    }
  }

  /* Check if there are any differences */
  const hasChanges = Object.keys(differences).length > 0;
  if (!hasChanges) {
    return undefined;
  }

  /* Return as array if input was array, otherwise as object */
  if (isArray) {
    const arrayResult: unknown[] = [];
    for (const key of Object.keys(differences)) {
      const index = parseInt(key);
      /* istanbul ignore else */
      if (!isNaN(index)) {
        arrayResult[index] = differences[key];
      }
    }
    return arrayResult;
  }

  return differences;
}
